<template>
  <div
    class="risk-assessment-launcher"
    :data-status-settled="String(isStatusSettled)"
  >
    <RiskAssessmentDialog
      v-if="isDialogOpen"
      :form="form"
      :hold-scope-label="draftStore?.scopeLabel ?? null"
      @close="closeDialog"
      @completed="handleCompleted"
      @report-requested="handleReportRequested"
    />
    <RiskAssessmentThanksDialog
      v-if="completionMessage"
      :message="completionMessage"
      @close="closeThanks"
      @report-requested="handleReportRequested"
    />
    <v-snackbar
      v-model="snackbar.open"
      color="error"
      location="top"
      :timeout="SNACKBAR_TIMEOUT"
    >
      {{ snackbar.message }}
      <template #actions>
        <v-btn
          variant="text"
          @click="snackbar.open = false"
        >
          閉じる
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
import { useStore } from "vuex";
import {
  canUseRiskAssessment,
  riskAssessmentsApi,
  type RiskAssessmentCompletionMessage,
  type RiskAssessmentConsultableType,
  type RiskAssessmentDeniedReason,
  type RiskAssessmentNewResponse,
  type RiskAssessmentResult,
  type RiskAssessmentStatusResponse
} from "@/vue/entities/risk-assessment";
import { formatDateTime } from "@/vue/features/risk-assessment-input/model/datetime";
import {
  deniedReasonMessage,
  injectRiskAssessmentDraftStore,
  resolveErrorMessage,
  RiskAssessmentDialog,
  RiskAssessmentThanksDialog,
  useRiskAssessmentDraft,
  useRiskAssessmentForm
} from "@/vue/features/risk-assessment-input";
import { FEATURE_FLAGS } from "@/vue/shared/utils/featureFlags";

const SNACKBAR_TIMEOUT = 6000;

const props = defineProps<{
  consultableType: RiskAssessmentConsultableType;
  consultableId: string;
}>();

export type RiskAssessmentLauncherStatus = {
  startable: boolean;
  deniedReason: RiskAssessmentDeniedReason | null;
  editable: boolean;
  editableUntil: string | null;
  // ボトムバーにそのまま表示できる形（YYYY/MM/DD HH:mm）。呼び出し側に整形を複製しない。
  editableUntilLabel: string;
};

const emit = defineEmits<{
  completed: [RiskAssessmentResult];
  "report-requested": [{ riskAssessmentId: string | null }];
  "status-changed": [RiskAssessmentLauncherStatus];
  "settled-changed": [boolean];
}>();

// 入力途中の内容は widget のインスタンス状態として保持し、ダイアログを閉じても消えない（FR-012）。
// 区分（夜間相談 / 日中助産師）がストアを provide している場合は、相談を離れる際にそこへ預け、戻ったら復元する。
// provide が無い画面（いつでも相談）では、カルテ画面の unmount で構造的に破棄される（REQ-010 / AC-004）。
const draft = useRiskAssessmentDraft();
const form = useRiskAssessmentForm(draft);
const draftStore = injectRiskAssessmentDraftStore();

const restoreDraft = (type: RiskAssessmentConsultableType, id: string): void => {
  const state = draftStore?.take(type, id);
  if (state) draft.restore(state);
};
restoreDraft(props.consultableType, props.consultableId);

const store = useStore();
// フラグB（#2599）有効時はボトムバーの導線ごと無効にし、表示判定のための API も呼ばない
const isPermitted = computed<boolean>(() => canUseRiskAssessment(store?.getters?.capabilities)
  && store?.getters?.isFeatureEnabled?.(FEATURE_FLAGS.hideHighRiskBarActions) !== true);

// ボトムバーのボタン構成は、権限の判定とステータス取得の結果を受けて後から変わる。
// 位置の安定だけでは取得の完了を保証できないため、system spec が確定を待てるよう DOM（data-status-settled）に公開する。
const hasSettledStatus = ref(false);
// 重複した取得のうち、最後に開始したものの完了だけを確定として扱うための番号。
let latestStatusRequest = 0;
const isStatusSettled = computed<boolean>(() => {
  // currentDoctor の取得前は権限が未確定で、この後に取得が始まりうる。
  if (store?.getters?.currentDoctor == null) return false;
  // 権限が無ければ取得せず、ボタン構成もこれ以上変わらない。
  if (!isPermitted.value) return true;
  return hasSettledStatus.value;
});
// 呼び出し側がボトムバーの表示を確定まで保留できるよう、確定状態を通知する。
watch(isStatusSettled, (settled) => emit("settled-changed", settled), { immediate: true });

const isDialogOpen = ref(false);
const isLoading = ref(false);
const completionMessage = ref<RiskAssessmentCompletionMessage | null>(null);
const completedAssessmentId = ref<string | null>(null);
const snackbar = reactive({ open: false, message: "" });

// カルテ切替後に届いた古い応答を破棄するための世代カウンタ。
const requestGeneration = ref(0);
const bumpRequestGeneration = (): number => {
  requestGeneration.value += 1;
  return requestGeneration.value;
};

const showSnackbar = (message: string): void => {
  snackbar.message = message;
  snackbar.open = true;
};

const discard = (): void => {
  draft.clear();
  form.clearError();
  form.clearRecovery();
  isDialogOpen.value = false;
  completionMessage.value = null;
  completedAssessmentId.value = null;
};

// 完了済み・送信中の内容を預けると、戻った時に同じ回答を二重に送信できてしまうため預けない。
const persistDraft = (type: RiskAssessmentConsultableType, id: string): void => {
  if (!draftStore) return;

  const persistable = completionMessage.value === null && !form.isSubmitting;
  draftStore.save(type, id, persistable ? draft.snapshot() : null);
};

const editableStatusOf = (
  assessment: RiskAssessmentNewResponse["editableAssessment"] | RiskAssessmentStatusResponse["editableAssessment"]
): boolean => assessment === null || !("editable" in assessment) || assessment.editable;

// ボトムバーのボタン活性判定と修正可能期限の表示に使う状態を呼び出し側へ渡す
// （期限計算をフロントに複製せず、サーバーの判定をそのまま使う / architecture.md 10）。
const publishStatus = (response: RiskAssessmentNewResponse | RiskAssessmentStatusResponse): void => {
  const editableUntil = response.editableAssessment?.editableUntil ?? null;

  emit("status-changed", {
    startable: response.startable,
    deniedReason: response.deniedReason,
    editable: editableStatusOf(response.editableAssessment),
    editableUntil: editableUntil,
    editableUntilLabel: formatDateTime(editableUntil)
  });
};

// 状態取得に失敗してもカルテ画面は壊さない。ボタンは活性のままにし、open() 時にサーバーが再判定する。
// カルテを開くたびに呼ばれるため、回答内容を含まない status を使う
// （開いてもいない要配慮個人情報を受け取らず、閲覧の監査ログも汚さない / NR-002 / NR-003）。
const loadStatus = async (): Promise<void> => {
  // 権限のないロールが呼ぶと 403 になり、共通インターセプタがホームへ強制遷移させてしまうため呼ばない。
  if (!isPermitted.value) return;

  const generation = requestGeneration.value;
  const statusRequest = ++latestStatusRequest;
  hasSettledStatus.value = false;
  try {
    const response = await riskAssessmentsApi.fetchStatus({
      consultableType: props.consultableType,
      consultableId: props.consultableId
    });
    if (generation !== requestGeneration.value) return;

    publishStatus(response);
  } catch {
    // no-op
  } finally {
    if (statusRequest === latestStatusRequest) hasSettledStatus.value = true;
  }
};

// capabilities はカルテ表示後に非同期で設定される場合があるため、
// 初回マウント時の1回だけでなく、リスクアセスメント権限が有効になった時点でも取得する。
watch(isPermitted, (permitted) => {
  if (permitted) loadStatus();
}, { immediate: true });

// 同一ルートで相談カルテだけが差し替わるケース。別カルテへ入力内容を引き継がせない。
watch(
  () => [props.consultableType, props.consultableId] as const,
  ([nextType, nextId], [previousType, previousId]) => {
    persistDraft(previousType, previousId);
    bumpRequestGeneration();
    isLoading.value = false;
    discard();
    restoreDraft(nextType, nextId);
    loadStatus();
  }
);

onBeforeUnmount(() => {
  persistDraft(props.consultableType, props.consultableId);
  emit("settled-changed", false);
});

const loadAndOpen = async (): Promise<void> => {
  if (isLoading.value) return;

  const generation = requestGeneration.value;
  isLoading.value = true;
  try {
    const response = await riskAssessmentsApi.fetchNew({
      consultableType: props.consultableType,
      consultableId: props.consultableId
    });
    if (generation !== requestGeneration.value) return;

    publishStatus(response);

    // 担当外・期間超過は 200 + startable: false で返るため、起動せずメッセージのみ表示する。
    if (!response.startable) {
      isDialogOpen.value = false;
      showSnackbar(deniedReasonMessage(response.deniedReason));
      return;
    }

    draft.initialize(response);
    isDialogOpen.value = true;
  } catch (error) {
    if (generation !== requestGeneration.value) return;

    isDialogOpen.value = false;
    showSnackbar(resolveErrorMessage(error));
  } finally {
    if (generation === requestGeneration.value) isLoading.value = false;
  }
};

// 保持中の入力は担当解除・期間超過後も残り得るため、再表示前に最新の操作可否を確認する。
// 確認に失敗した場合は開き、完了時のサーバー再判定に委ねる。
const reopenRetained = async (): Promise<void> => {
  if (isLoading.value) return;

  const generation = requestGeneration.value;
  isLoading.value = true;
  try {
    const response = await riskAssessmentsApi.fetchStatus({
      consultableType: props.consultableType,
      consultableId: props.consultableId
    });
    if (generation !== requestGeneration.value) return;

    publishStatus(response);
    if (!response.startable) {
      discard();
      showSnackbar(deniedReasonMessage(response.deniedReason));
      return;
    }
  } catch {
    if (generation !== requestGeneration.value) return;
  } finally {
    if (generation === requestGeneration.value) isLoading.value = false;
  }

  isDialogOpen.value = true;
};

const open = async (): Promise<void> => {
  // 保持中の入力がある場合は GET /new を再取得せず、そのまま再開する。
  if (draft.isRetained.value) {
    await reopenRetained();
    return;
  }

  await loadAndOpen();
};

// 保持中の内容を捨てて GET /new を取り直す。捨てないと isRetained が true のままで再取得されない。
const reload = async (): Promise<void> => {
  form.clearRecovery();
  draft.clear();
  await loadAndOpen();
};

// stale_object は文言どおり自動で最新を読み直す。edit_period_expired は入力を終了する。
watch(
  () => form.recoveryCode,
  (code) => {
    if (code === "stale_object") void reload();
  }
);

const closeDialog = (): void => {
  isDialogOpen.value = false;
};

// 保持内容の破棄はサンクスダイアログを閉じた時点（dataflow.md 3 / test-plan.md FD-4）。
const closeThanks = (): void => {
  completionMessage.value = null;
  completedAssessmentId.value = null;
  // 次回起動時は GET /new を取り直し、修正モード（4時間以内）として開く。
  draft.clear();
  // 完了直後に修正可能期限が発生するため、ボトムバーの表示を更新する。
  loadStatus();
};

const handleCompleted = (result: RiskAssessmentResult): void => {
  completedAssessmentId.value = result.id;
  completionMessage.value = result.completionMessage;
  isDialogOpen.value = false;
  emit("completed", result);
};

const handleReportRequested = (): void => {
  const riskAssessmentId = completedAssessmentId.value;
  // #2235 の報告ダイアログが入力・サンクスダイアログに重ならないよう、先に閉じる。
  if (completionMessage.value) closeThanks();
  isDialogOpen.value = false;
  snackbar.open = false;
  emit("report-requested", { riskAssessmentId });
};

defineExpose({ open, isLoading });
</script>
